import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import OnlineMeetingIcon from "@mui/icons-material/PersonalVideo";
import PlaceIcon from "@mui/icons-material/Place";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StudentIcon from "@mui/icons-material/PersonOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { EventContentArg } from "@fullcalendar/core";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import isString from "lodash/isString";
import {
  ASFCourseSession,
  AutovioCalendarEvent,
  CourseLesson,
  DrivingLesson,
  hasInstructor,
  isASFCourseSession,
  isCourseLesson,
  isDrivingLesson,
  isOtherEvent,
  isRemoteLesson,
  isTheoryExam,
  isTheoryLesson,
  isVehicleEvent,
  OtherEvent,
  PracticalExam,
  RemoteLesson,
  TheoryExam,
  TheoryLesson,
  VehicleEvent,
} from "../../model/autovioCalendarEvents";
import { calendarEventTitle, formatTime } from "../../utils/calendar";
import { InstructorDisplayName } from "../InstructorDisplayName";
import { StudentDisplayName } from "../StudentDisplayName";
import { EventDialog } from "../../dialogs/EventDialog";
import { Recommendation, RecommendationWithPrice } from "../../api/backoffice.api";
import { useRecoilValue } from "recoil";
import { selectedStartLocationState, useIsDebug } from "./InstructorCalendar";
import { LinearProgress, RecordContextProvider, TextField, useGetOne, useRecordContext } from "react-admin";
import { type Instructor } from "../../providers/instructorsProvider";
import { ConfirmBookingDialog } from "./ConfirmBookingDialog";
import { type DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { type Vehicle } from "../../providers/vehiclesProvider";
import { VehicleIcon } from "../../icons/VehicleIcon";
import { Row } from "../Row";
import { HoverContainer } from "../HoverContainer";
import { IconWithText } from "../IconWithText";
import { type Course, isCourseEvent } from "../../providers/coursesProvider";
import { type Room } from "../../providers/roomsProvider";
import { useExamAttempt } from "../../hooks/useExamAttempt";
import { RoomIcon } from "../../icons/RoomIcon";
import { UserDisplayName } from "../UserDisplayName";
import { InstructorIcon } from "../../icons/InstructorIcon";
import NotesIcon from "@mui/icons-material/Notes";
import { type Student } from "../../providers/studentsProvider";
import { CheckedIcon } from "../../icons/CheckedIcon";
import { UncheckedOutlineIcon } from "../../icons/UncheckedOutlineIcon";
import { autovioColors } from "../backofficeTheme";
import { useThemeColor } from "../../hooks/useThemeColor";
import { useFormState } from "react-hook-form";
import { FreeSlot } from "../../dialogs/CourseDetailsDialog/FreeSlot";
import { bulkEditSelectedEventsIdsAtom } from "../../dialogs/CourseDetailsDialog/BulkEditForm";
import { renderLocation } from "../../fields/LocationField";
import { usePracticalExamDrivingLicenseClass } from "../../hooks/usePracticalExamDrivingLicenseClass";
import { l10n } from "../../l10n";

export function EventContent(props: {
  readonly eventInfo: EventContentArg;
  readonly mode:
    | "CourseEventsCalendar"
    | "DrivingSchoolExamsCalendar"
    | "InstructorCalendar"
    | "ResourceCalendar"
    | "bulkEdit";
}) {
  const { extendedProps } = props.eventInfo.event;
  const { event, warnings, recommendation, course, start, end, extraDetails, bulkEdit, onPreviewHoverChange } =
    extendedProps as {
      event?: AutovioCalendarEvent;
      warnings?: Array<string>;
      recommendation?: Recommendation;
      course?: Course;
      start?: DateTime;
      end?: DateTime;
      extraDetails: Record<string, unknown>;
      bulkEdit?: { onClick: () => void };
      onPreviewHoverChange?: (hovered: boolean) => void;
    };
  if (event) {
    return (
      <AutovioCalendarEventContent
        eventInfo={props.eventInfo}
        event={event}
        warnings={warnings}
        mode={props.mode}
        bulkEdit={props.mode === "bulkEdit" || props.mode === "CourseEventsCalendar" ? bulkEdit : undefined}
      />
    );
  }
  if (recommendation) {
    return <RecommendationContent eventInfo={props.eventInfo} recommendation={recommendation} />;
  }
  if (course && start && end && props.eventInfo.event.id.startsWith("free_slot_")) {
    return <FreeSlot course={course} start={start} end={end} onPreviewHoverChange={onPreviewHoverChange} />;
  }
  return <GenericEventContent eventInfo={props.eventInfo} extraDetails={extraDetails} />;
}

function AutovioCalendarEventContent(props: {
  eventInfo: EventContentArg;
  event: AutovioCalendarEvent;
  warnings?: Array<string>;
  mode: "CourseEventsCalendar" | "DrivingSchoolExamsCalendar" | "InstructorCalendar" | "ResourceCalendar" | "bulkEdit";
  bulkEdit: undefined | { onClick: () => void };
}) {
  const { eventInfo, event, warnings, mode, bulkEdit } = props;
  const openDialogParam = `${event.type}Dialog!${event.id}`;
  const [isDialogOpen, setIsDialogOpen_] = useState<boolean>(() => {
    if (mode === "bulkEdit") {
      return false;
    }
    const openDialogParams = new URL(location.href).searchParams.getAll("openDialog");
    return openDialogParams.includes(openDialogParam);
  });
  const setIsDialogOpen = (value: boolean) => {
    // Side effect: update browser URL ...
    const url = new URL(location.href);
    const openDialogParams = url.searchParams.getAll("openDialog");
    if (value) {
      if (!openDialogParams.includes(openDialogParam)) {
        url.searchParams.append("openDialog", openDialogParam);
        browserHistory.replace(url.toString());
      }
    } else {
      url.searchParams.delete("openDialog");
      for (const param of openDialogParams) {
        if (param !== openDialogParam) {
          url.searchParams.append("openDialog", param);
        }
      }
      url.searchParams.delete("showHistory");
      browserHistory.replace(url.toString());
    }
    setIsDialogOpen_(value);
  };

  return (
    <EventContentWithTooltip
      eventInfo={eventInfo}
      warnings={warnings}
      onClick={mode === "bulkEdit" ? bulkEdit?.onClick : () => setIsDialogOpen(true)}
      tooltipDisabled={isDialogOpen}
      tooltip={
        isDrivingLesson(event) ? (
          <CalendarEventCardContent event={event} warnings={warnings} mode="Tooltip" bulkEdit={undefined} />
        ) : undefined
      }
    >
      <CalendarEventCardContent event={event} warnings={warnings} mode={mode} bulkEdit={bulkEdit} />
      {mode !== "bulkEdit" && (
        <EventDialog event={event} isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
      )}
    </EventContentWithTooltip>
  );
}

function GenericEventContent(props: {
  readonly eventInfo: EventContentArg;
  readonly extraDetails?: Record<string, unknown>;
}) {
  const title = <EventTitle eventOrTitle={props.eventInfo.event.title} />;
  const subTitle = isString(props?.extraDetails?.subtitle) ? (
    <Typography variant="subtitle2" fontWeight={400}>
      {props.extraDetails.subtitle}
    </Typography>
  ) : null;
  return (
    <EventContentWithTooltip eventInfo={props.eventInfo}>
      {title}
      {subTitle}
    </EventContentWithTooltip>
  );
}

function RecommendationContent(props: {
  readonly eventInfo: EventContentArg;
  readonly recommendation: Recommendation | RecommendationWithPrice;
}) {
  const instructor = useRecordContext<Instructor>();
  const [isConfirmBookingDialogOpen, setIsConfirmBookingDialogOpen] = useState(false);
  const { data: drivingSchool } = useGetOne("drivingSchools", { id: instructor.drivingSchoolId });
  const startLocation = useRecoilValue(selectedStartLocationState);

  const content = (
    <>
      <RecommendationCardContent recommendation={props.recommendation} />
      {"price" in props.recommendation && drivingSchool && startLocation && (
        <>
          <HoverContainer onClick={() => setIsConfirmBookingDialogOpen(true)} />
          <ConfirmBookingDialog
            isOpen={isConfirmBookingDialogOpen}
            drivingSchool={drivingSchool}
            startLocation={startLocation}
            recommendation={props.recommendation}
            onClose={() => setIsConfirmBookingDialogOpen(false)}
          />
        </>
      )}
    </>
  );

  return (
    <EventContentWithTooltip eventInfo={props.eventInfo} onClick={() => setIsConfirmBookingDialogOpen(true)}>
      {content}
    </EventContentWithTooltip>
  );
}

interface CalendarEventContentProps<T extends AutovioCalendarEvent> {
  event: T;
  warnings?: Array<string>;
  bulkEdit?: { onClick: () => void };
  mode:
    | "CourseEventsCalendar"
    | "DrivingSchoolExamsCalendar"
    | "InstructorCalendar"
    | "ResourceCalendar"
    | "bulkEdit"
    | "Tooltip"
    | "YearViewTooltip";
}

export function CalendarEventCardContent(props: CalendarEventContentProps<AutovioCalendarEvent>) {
  const { event, warnings, mode, bulkEdit } = props;
  if (isDrivingLesson(event)) {
    return <DrivingLessonContent event={event} warnings={warnings} mode={mode} />;
  } else if (isTheoryLesson(event)) {
    return <TheoryLessonContent event={event} warnings={warnings} mode={mode} />;
  } else if (isOtherEvent(event)) {
    return <OtherEventContent event={event} warnings={warnings} mode={mode} />;
  } else if (isTheoryExam(event)) {
    return <TheoryExamContent event={event} warnings={warnings} mode={mode} />;
  } else if (isRemoteLesson(event)) {
    return <RemoteLessonContent event={event} warnings={warnings} mode={mode} />;
  } else if (isASFCourseSession(event)) {
    return <ASFCourseSessionContent event={event} warnings={warnings} mode={mode} />;
  } else if (isCourseLesson(event)) {
    return <CourseLessonContent event={event} warnings={warnings} mode={mode} bulkEdit={bulkEdit} />;
  } else if (isVehicleEvent(event)) {
    return <VehicleEventContent event={event} warnings={warnings} mode={mode} bulkEdit={bulkEdit} />;
  }
  // Fallback for unknown events ...
  return <Typography>Fehler beim Anzeigen</Typography>;
}

function _TopRightIcons(props: { isPending?: boolean; hasWarning?: boolean }) {
  const { isPending, hasWarning } = props;
  if (!isPending && !hasWarning) {
    return null;
  }
  return (
    <div style={{ position: "absolute", top: 0, right: 0, display: "flex", gap: "4px", alignItems: "center" }}>
      {isPending && <HourglassEmptyIcon fontSize="small" sx={{ opacity: 0.5 }} />}
      {hasWarning && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "2px 4px 5px 4px",
            backgroundColor: autovioColors.white,
            borderRadius: "50%",
            mt: "2px",
          }}
        >
          <WarningAmberRoundedIcon fontSize="small" sx={{ color: autovioColors.red }} />
        </Box>
      )}
    </div>
  );
}

function ExamThumbIconTopRight(props: { status: "passed" | "failed" | "mixed" }) {
  const { status } = props;
  if (status === "mixed") return "👍👎";
  return status === "passed" ? "👍" : "👎";
}

function EventTitle(props: {
  readonly eventOrTitle: AutovioCalendarEvent | string;
  readonly drivingLicenseClass?: string;
}) {
  const { eventOrTitle, drivingLicenseClass } = props;
  const examAttempt = useExamAttempt(typeof eventOrTitle === "string" ? undefined : eventOrTitle);
  const practicalExamDrivingLicenseClass = usePracticalExamDrivingLicenseClass(
    typeof eventOrTitle === "string" ? undefined : eventOrTitle,
  );
  const displayedDrivingLicenseClass = practicalExamDrivingLicenseClass ?? drivingLicenseClass;
  const title =
    typeof eventOrTitle === "string"
      ? eventOrTitle
      : `${examAttempt ? `${examAttempt}. ` : ""}${calendarEventTitle(eventOrTitle)}`;
  return (
    <Typography variant="body2" fontWeight="bold" sx={{ display: "flex", minWidth: 0 }}>
      <Box
        component="span"
        sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {title}
      </Box>
      {displayedDrivingLicenseClass && (
        <Box component="span" sx={{ flexShrink: 0, ml: 0.5, whiteSpace: "nowrap" }}>
          {`(${displayedDrivingLicenseClass})`}
        </Box>
      )}
    </Typography>
  );
}

function DrivingLessonContent(props: CalendarEventContentProps<DrivingLesson | PracticalExam>) {
  const { event, warnings, mode } = props;
  const isPending = event.student?.rsvp === "pending";
  const { data: student } = useGetOne<Student>("students", { id: event.student.uid });
  const eventDrivingLicenseClass =
    event.drivingLicenseClass ??
    (event.student.bookedTrainingId
      ? student?.bookedTrainingsById[event.student.bookedTrainingId]?.drivingLicenseClass
      : undefined);

  return (
    <Stack sx={{ position: "relative" }}>
      {mode !== "Tooltip" && <_TopRightIcons isPending={isPending} hasWarning={!!warnings?.length} />}
      <Row gap={0.5} alignItems="center">
        <EventTitle
          eventOrTitle={event}
          drivingLicenseClass={
            mode === "InstructorCalendar"
              ? (eventDrivingLicenseClass ?? student?.activeOrMostRecentBookedTraining.drivingLicenseClass)
              : undefined
          }
        />
        {!isPending && mode !== "Tooltip" && event.practicalExamResult && (
          <ExamThumbIconTopRight status={event.practicalExamResult} />
        )}
      </Row>
      {mode === "Tooltip" &&
        warnings &&
        warnings.map((warning) => (
          <Row alignItems="center" gap={0.25} sx={{ color: autovioColors.redLight }}>
            <WarningAmberRoundedIcon fontSize="small" />
            <Typography variant="caption">{warning}</Typography>
          </Row>
        ))}
      {mode === "Tooltip" && (
        <IconWithText
          icon={<ScheduleIcon fontSize="inherit" />}
          text={`${formatTime(event.start)} - ${formatTime(event.end)} Uhr`}
        />
      )}
      {mode === "Tooltip" && <_Instructor event={event} />}
      {mode === "Tooltip" || mode === "InstructorCalendar" ? (
        <_Student studentId={event.student.uid} />
      ) : (
        <IconWithText
          icon={<StudentIcon fontSize="inherit" />}
          text={
            <Stack direction="row" gap="3px">
              <StudentDisplayName studentId={event.student.uid} fontSize="11px" noWrap />
              {hasInstructor(event) && (
                <Stack direction="row">
                  <Typography fontSize="11px" noWrap>
                    {"("}
                  </Typography>
                  <InstructorDisplayName instructorId={event.instructorId!} source="firstName" fontSize="11px" noWrap />
                  <Typography fontSize="11px" noWrap>
                    {")"}
                  </Typography>
                </Stack>
              )}
            </Stack>
          }
        />
      )}
      {mode === "Tooltip" && event.studentUsesOwnMotorcycle && (
        <IconWithText
          icon={<VehicleIcon type="motorcycle" fontSize="inherit" />}
          text={
            <Typography component="span" variant="body2" fontSize={"11px"} noWrap>
              eigenes Motorrad
            </Typography>
          }
        />
      )}
      {mode === "Tooltip" && (event.resources ?? []).map((id) => <_Vehicle key={id} vehicleId={id} />)}
      {mode === "Tooltip" && <_Location event={event} />}
    </Stack>
  );
}

function TheoryLessonContent(props: CalendarEventContentProps<TheoryLesson>) {
  const { mode, event } = props;
  const numStudents = Object.values(event.students).filter((it) => it.rsvp === "accepted" && !it.noShow).length;
  const { maxStudents } = event;
  return (
    <Stack sx={{ position: "relative" }}>
      {mode === "YearViewTooltip" && event.start.toFormat("HH:mm") + "\u00A0Uhr"}
      <EventTitle eventOrTitle={l10n.lectureXxx(event)} />
      <_Instructor event={event} />
      <_Location event={event} />
      <IconWithText icon={<PeopleIcon fontSize="inherit" />} text={`${numStudents}/${maxStudents} Teilnehmer`} />
    </Stack>
  );
}

function ASFCourseSessionContent(props: CalendarEventContentProps<ASFCourseSession>) {
  const { event, mode } = props;
  return (
    <Stack sx={{ position: "relative" }}>
      <EventTitle eventOrTitle={calendarEventTitle(event)} />
      {mode !== "InstructorCalendar" && <_Instructor event={event} />}
      <_Location event={props.event} />
      <IconWithText icon={<PeopleIcon fontSize="inherit" />} text={`${event.numBookedParticipants} Teilnehmer`} />
    </Stack>
  );
}

function CourseLessonContent(props: CalendarEventContentProps<CourseLesson>) {
  const { event, mode, bulkEdit } = props;
  const showBulkEditCheckbox = !!bulkEdit && (mode === "bulkEdit" || mode === "CourseEventsCalendar");
  return (
    <Stack
      sx={{
        position: "relative",
        height: "100%",
      }}
    >
      {showBulkEditCheckbox && (
        <_Checkbox eventId={event.id} onClick={bulkEdit.onClick} visibleOnHover={mode === "CourseEventsCalendar"} />
      )}
      <EventTitle eventOrTitle={calendarEventTitle(event)} />
      {mode !== "InstructorCalendar" && event.lecturerId && <_Lecturer lecturerId={event.lecturerId} />}
      <_Room roomId={event.roomId} />
      {event.noteForParticipants && (
        <IconWithText icon={<NotesIcon fontSize="inherit" />} text={event.noteForParticipants} />
      )}
    </Stack>
  );
}

function VehicleEventContent(props: CalendarEventContentProps<VehicleEvent>) {
  const { event, mode, bulkEdit } = props;
  const showBulkEditCheckbox = !!bulkEdit && (mode === "bulkEdit" || mode === "CourseEventsCalendar");
  return (
    <Stack
      sx={{
        position: "relative",
        height: "100%",
      }}
    >
      {showBulkEditCheckbox && (
        <_Checkbox eventId={event.id} onClick={bulkEdit.onClick} visibleOnHover={mode === "CourseEventsCalendar"} />
      )}
      <EventTitle eventOrTitle={calendarEventTitle(event)} />
      {event.noteForInstructor && (
        <IconWithText icon={<NotesIcon fontSize="inherit" />} text={event.noteForInstructor} />
      )}
    </Stack>
  );
}

function _Checkbox(props: { eventId: string; onClick: () => void; visibleOnHover: boolean }) {
  const { eventId, onClick, visibleOnHover } = props;
  const selectedEventsIds = useRecoilValue(bulkEditSelectedEventsIdsAtom);
  const isSelected = selectedEventsIds.has(eventId);
  const primaryColor = useThemeColor("primary");
  const { isSubmitted } = useFormState();
  return (
    <div
      className={`BulkEditCheckbox${visibleOnHover ? " BulkEditCheckbox-hoverOnly" : ""}`}
      style={{ position: "absolute", right: "0px", bottom: "5px", zIndex: 1 }}
    >
      <div style={{ backgroundColor: "white", borderRadius: "50%", display: "flex" }}>
        <button
          type="button"
          aria-label={isSelected ? "Termin abwählen" : "Termin auswählen"}
          style={{ all: "unset", cursor: "pointer", display: "flex" }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClick();
          }}
        >
          {isSelected ? (
            <CheckedIcon sx={{ color: primaryColor.main }} />
          ) : (
            <UncheckedOutlineIcon
              sx={{ color: selectedEventsIds.size === 0 && isSubmitted ? "error.main" : "divider" }}
            />
          )}
        </button>
      </div>
    </div>
  );
}

function OtherEventContent(props: CalendarEventContentProps<OtherEvent>) {
  const { event, mode } = props;
  return (
    <Stack>
      <EventTitle eventOrTitle={event} />
      {mode !== "InstructorCalendar" && <_Instructor event={event} />}
      {event.notes && (
        <Typography fontSize="11px" noWrap>
          {event.notes}
        </Typography>
      )}
    </Stack>
  );
}

function TheoryExamContent(props: CalendarEventContentProps<TheoryExam>) {
  const { event, mode } = props;
  const perTrainingResults = Object.values(event.theoryExamResults ?? {});
  const hasMixedResults =
    perTrainingResults.length > 1 && perTrainingResults.some((it) => it !== perTrainingResults[0]);
  const examIconStatus = hasMixedResults ? "mixed" : (perTrainingResults[0] ?? event.theoryExamResult);
  return (
    <Stack>
      <Row gap={0.5} alignItems="center">
        <EventTitle eventOrTitle={event} />
        {examIconStatus && <ExamThumbIconTopRight status={examIconStatus} />}
      </Row>
      <_Student studentId={event.student.uid} />
      {mode === "Tooltip" && <_Location event={event} />}
    </Stack>
  );
}

function RemoteLessonContent(props: CalendarEventContentProps<RemoteLesson>) {
  const { event, mode } = props;
  return (
    <Stack>
      <EventTitle eventOrTitle={props.event} />
      {mode !== "InstructorCalendar" && <_Instructor event={event} />}
      <_Student studentId={event.student.uid} />
    </Stack>
  );
}

function RecommendationCardContent(props: { readonly recommendation: Recommendation }) {
  const isDebug = useIsDebug();
  return (
    <Stack>
      <EventTitle eventOrTitle={"Terminvorschlag"} />
      {props.recommendation.resourceUids.map((id) => (
        <_Vehicle key={id} vehicleId={id} />
      ))}
      {isDebug && props.recommendation.score !== undefined && (
        <Typography>{`Score: ${Math.round(props.recommendation.score)}`}</Typography>
      )}
      {isDebug && props.recommendation.individualScores?.coverage !== undefined && (
        <Typography>{`Coverage: ${Math.round(props.recommendation.individualScores?.coverage)}`}</Typography>
      )}
      {isDebug && props.recommendation.individualScores?.instructorSwitch !== undefined && (
        <Typography>{`Instructor Switch: ${Math.round(
          props.recommendation.individualScores?.instructorSwitch,
        )}`}</Typography>
      )}
      {isDebug && props.recommendation.individualScores?.resourceSwitch !== undefined && (
        <Typography>{`Resource Switch: ${Math.round(
          props.recommendation.individualScores?.resourceSwitch,
        )}`}</Typography>
      )}
      {isDebug && props.recommendation.removeReason && (
        <Typography>{`Reason: ${props.recommendation.removeReason}`}</Typography>
      )}
    </Stack>
  );
}

function EventContentWithTooltip(
  props: React.PropsWithChildren<{
    eventInfo: EventContentArg;
    warnings?: Array<string>;
    onClick?: (eventInfo: EventContentArg) => void;
    tooltip?: React.ReactNode;
    tooltipDisabled?: boolean;
  }>,
) {
  const { eventInfo, onClick, children, tooltip, tooltipDisabled } = props;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (tooltipDisabled) {
      setIsTooltipOpen(false);
    }
  }, [tooltipDisabled]);

  const content = (
    <Box overflow="hidden" sx={{ height: "100%" }}>
      <Box
        sx={{
          px: "5px",
          height: "100%",
          "& .BulkEditCheckbox-hoverOnly": { display: "none" },
          "&:hover .BulkEditCheckbox-hoverOnly": { display: "block" },
        }}
      >
        {children}
        {onClick && (
          <HoverContainer
            onClick={() => {
              setIsTooltipOpen(false);
              onClick(eventInfo);
            }}
          />
        )}
      </Box>
    </Box>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      enterDelay={300}
      disableInteractive
      disableFocusListener={tooltipDisabled}
      disableHoverListener={tooltipDisabled}
      disableTouchListener={tooltipDisabled}
      open={!tooltipDisabled && isTooltipOpen}
      onOpen={() => {
        if (!tooltipDisabled) {
          setIsTooltipOpen(true);
        }
      }}
      onClose={() => setIsTooltipOpen(false)}
    >
      {content}
    </Tooltip>
  );
}

function _Instructor({ event }: { event: AutovioCalendarEvent }) {
  const { instructorId } = event;
  if (!instructorId) {
    return null;
  }
  return (
    <IconWithText
      icon={
        isTheoryLesson(event) || (isASFCourseSession(event) && event.sessionType !== "fahrprobe") ? (
          <InstructorIcon fontSize="inherit" />
        ) : (
          <PersonIcon fontSize="inherit" />
        )
      }
      text={<InstructorDisplayName instructorId={instructorId} fontSize="11px" noWrap />}
    />
  );
}

function _Student({ studentId }: { studentId: string }) {
  return (
    <IconWithText
      icon={<StudentIcon fontSize="inherit" />}
      text={<StudentDisplayName studentId={studentId} fontSize="11px" noWrap />}
    />
  );
}

function _Vehicle({ vehicleId }: { vehicleId: string }) {
  const { data: vehicle } = useGetOne<Vehicle>("vehicles", { id: vehicleId });
  if (!vehicle) {
    return <LinearProgress sx={{ width: "75%" }} />;
  }
  return (
    <RecordContextProvider value={vehicle}>
      <IconWithText
        icon={<VehicleIcon vehicle={vehicle} fontSize="inherit" />}
        text={<TextField source="name" fontSize={"11px"} noWrap />}
      />
    </RecordContextProvider>
  );
}

function _Location(props: {
  readonly event: DrivingLesson | TheoryLesson | TheoryExam | ASFCourseSession | CourseLesson;
}) {
  const { event } = props;
  const { drivingSchoolId } = event;
  const { data: drivingSchool } = useGetOne<DrivingSchool>("drivingSchools", { id: drivingSchoolId });
  const location = isDrivingLesson(event) || isTheoryExam(event) ? event.startLocation : event.location;
  const { data: room } = useGetOne<Room>("rooms", { id: event.roomId }, { enabled: !!event.roomId });

  if (location.type === "OnlineMeeting") {
    return <IconWithText icon={<OnlineMeetingIcon fontSize="inherit" />} text="Online" />;
  }

  if (!drivingSchool) {
    return <LinearProgress sx={{ width: "75%" }} />;
  }

  if (isCourseEvent(event)) {
    const { branchId } = event;
    if (branchId) {
      const branch = drivingSchool.branchesById[branchId];
      if (branch) {
        return (
          <IconWithText
            icon={<PlaceIcon fontSize="inherit" />}
            text={isCourseLesson(event) && room ? `${branch.name} (${room.name})` : branch.name}
          />
        );
      }
    }
  }

  return <IconWithText icon={<PlaceIcon fontSize="inherit" />} text={renderLocation(event)} />;
}

function _Lecturer(props: { lecturerId: undefined | null | string }) {
  const { lecturerId } = props;
  if (!lecturerId) {
    return null;
  }
  return (
    <IconWithText
      icon={<InstructorIcon fontSize="inherit" />}
      text={<UserDisplayName userId={lecturerId} fontSize="11px" noWrap />}
    />
  );
}

function _Room(props: { roomId: undefined | null | string }) {
  const roomId = props.roomId ?? "";
  const { data: room } = useGetOne<Room>("rooms", { id: roomId }, { enabled: !!roomId });

  if (!roomId) {
    return null;
  }

  return (
    <IconWithText
      icon={<RoomIcon fontSize="inherit" />}
      text={room ? room.name : <LinearProgress sx={{ mt: "5px", maxWidth: "8em" }} />}
    />
  );
}
